import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { formatCurrency, formatDate } from '../../lib/utils';
import { 
  History, Search, Filter, Eye, Truck, XCircle, Ban,
  Clock, Package, AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  delivered: { label: 'Entregue', color: 'bg-green-100 text-green-800', icon: Truck },
  cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-800', icon: XCircle },
  rejected: { label: 'Rejeitado', color: 'bg-red-100 text-red-800', icon: Ban }
};

export default function OrderHistory() {
  const { user } = useAuth();
  const [establishment, setEstablishment] = useState(null);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchEstablishment = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/establishments`);
      if (response.data.length > 0) {
        setEstablishment(response.data[0]);
        setError(null);
      } else {
        setError('no_establishment');
      }
    } catch (err) {
      console.error('Error fetching establishment:', err);
      setError('fetch_error');
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    if (!establishment) return;
    try {
      const response = await axios.get(`${API}/establishments/${establishment.id}/orders/history`);
      setOrders(response.data);
      setFilteredOrders(response.data);
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  }, [establishment]);

  useEffect(() => {
    fetchEstablishment();
  }, [fetchEstablishment]);

  useEffect(() => {
    if (establishment) {
      fetchOrders();
    }
  }, [establishment, fetchOrders]);

  // Apply filters
  useEffect(() => {
    let result = orders;
    
    if (statusFilter !== 'all') {
      result = result.filter(order => order.status === statusFilter);
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(order => 
        order.order_number.toLowerCase().includes(term) ||
        order.customer_name.toLowerCase().includes(term) ||
        order.customer_phone.includes(term)
      );
    }
    
    setFilteredOrders(result);
  }, [orders, statusFilter, searchTerm]);

  const getStatusStats = () => {
    return {
      delivered: orders.filter(o => o.status === 'delivered').length,
      cancelled: orders.filter(o => o.status === 'cancelled').length,
      rejected: orders.filter(o => o.status === 'rejected').length,
    };
  };

  const stats = getStatusStats();

  if (loading) {
    return (
      <AdminLayout establishmentName={establishment?.name}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error === 'no_establishment') {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center h-[60vh] text-center">
          <AlertCircle className="w-16 h-16 text-amber-500 mb-4" />
          <h2 className="text-2xl font-bold text-slate-700 mb-2">Nenhum estabelecimento vinculado</h2>
          <p className="text-slate-500">Entre em contato com o administrador do sistema.</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout establishmentName={establishment?.name}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2" data-testid="history-title">
            <History className="w-6 h-6" />
            Histórico de Pedidos
          </h1>
          <p className="text-slate-500">Pedidos finalizados, cancelados e rejeitados</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card data-testid="stats-delivered">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Entregues</p>
                  <p className="text-2xl font-bold text-green-600">{stats.delivered}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Truck className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stats-cancelled">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Cancelados</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.cancelled}</p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stats-rejected">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Rejeitados</p>
                  <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <Ban className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar por número, nome ou telefone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filtrar por status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="delivered">Entregues</SelectItem>
              <SelectItem value="cancelled">Cancelados</SelectItem>
              <SelectItem value="rejected">Rejeitados</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Orders List */}
        <Card>
          <CardContent className="p-0">
            {filteredOrders.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Nenhum pedido encontrado</p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredOrders.map(order => (
                  <OrderRow 
                    key={order.id} 
                    order={order} 
                    onView={() => setSelectedOrder(order)}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Order Detail Dialog */}
        <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
          <DialogContent className="max-w-lg" data-testid="order-detail-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>Pedido {selectedOrder?.order_number}</span>
                {selectedOrder && (
                  <StatusBadge status={selectedOrder.status} />
                )}
              </DialogTitle>
            </DialogHeader>
            {selectedOrder && (
              <div className="space-y-4">
                {/* Rejection Reason */}
                {(selectedOrder.status === 'rejected' || selectedOrder.status === 'cancelled') && 
                  selectedOrder.rejection_reason && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-red-800 flex items-center gap-2">
                      <Ban className="w-4 h-4" />
                      Motivo da rejeição
                    </p>
                    <p className="text-sm text-red-700 mt-1">{selectedOrder.rejection_reason}</p>
                  </div>
                )}
                
                {/* Customer Info */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-medium text-slate-700 mb-2">Dados do Cliente</h4>
                  <div className="space-y-1 text-sm">
                    <p><strong>Nome:</strong> {selectedOrder.customer_name}</p>
                    <p><strong>Telefone:</strong> {selectedOrder.customer_phone}</p>
                    {selectedOrder.customer_address && (
                      <p><strong>Endereço:</strong> {selectedOrder.customer_address}</p>
                    )}
                    <p><strong>Tipo:</strong> {selectedOrder.delivery_type === 'delivery' ? 'Entrega' : 'Retirada'}</p>
                    <p><strong>Pagamento:</strong> {selectedOrder.payment_method}</p>
                  </div>
                </div>

                {/* Items */}
                <div>
                  <h4 className="font-medium text-slate-700 mb-2">Itens do Pedido</h4>
                  <div className="space-y-2">
                    {selectedOrder.items.map((item, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <div>
                          <span>{item.quantity}x {item.product_name}</span>
                          {item.addons?.length > 0 && (
                            <div className="text-xs text-slate-500 ml-4">
                              {item.addons.map((addon, ai) => (
                                <p key={ai}>+ {addon.quantity}x {addon.name}</p>
                              ))}
                            </div>
                          )}
                        </div>
                        <span className="font-medium">{formatCurrency(item.unit_price * item.quantity)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Total */}
                <div className="border-t pt-4 flex justify-between items-center">
                  <span className="font-semibold text-lg">Total</span>
                  <span className="font-bold text-lg text-slate-800">{formatCurrency(selectedOrder.total)}</span>
                </div>

                {/* Dates */}
                <div className="text-xs text-slate-500 border-t pt-3 space-y-1">
                  <p><Clock className="w-3 h-3 inline mr-1" /> Criado em: {formatDate(selectedOrder.created_at)}</p>
                  <p><Clock className="w-3 h-3 inline mr-1" /> Atualizado em: {formatDate(selectedOrder.updated_at)}</p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}

function OrderRow({ order, onView }) {
  const config = STATUS_CONFIG[order.status] || STATUS_CONFIG.delivered;
  
  return (
    <div 
      className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
      data-testid={`order-row-${order.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 mb-1">
          <span className="font-mono text-sm font-semibold text-slate-800">{order.order_number}</span>
          <StatusBadge status={order.status} />
        </div>
        <p className="text-sm text-slate-600 truncate">{order.customer_name}</p>
        <p className="text-xs text-slate-400">{formatDate(order.created_at)}</p>
        
        {/* Show rejection reason inline */}
        {(order.status === 'rejected' || order.status === 'cancelled') && order.rejection_reason && (
          <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
            <Ban className="w-3 h-3" />
            {order.rejection_reason}
          </p>
        )}
      </div>
      <div className="flex items-center gap-4">
        <span className="font-semibold text-slate-800">{formatCurrency(order.total)}</span>
        <Button 
          size="sm" 
          variant="outline" 
          onClick={onView}
          data-testid={`view-order-${order.id}`}
        >
          <Eye className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.delivered;
  const Icon = config.icon;
  
  return (
    <Badge className={`${config.color} flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
}
